import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * LoginPage — full login flow with forgot password, reset, and confirmation.
 *
 * Uses react-router-dom for navigation, react-i18next for translations,
 * and lucide-react for icons — matching the ecosystem standard.
 *
 * Translation namespace: "common", keys prefixed with "auth.".
 * Products must provide these keys in their i18n config.
 */
import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import { confirmResetPassword, confirmSignUp, resendSignUpCode, resetPassword, signIn, } from "aws-amplify/auth";
import { Eye, EyeOff, Lock, Mail, X } from "lucide-react";
import { useAuth } from "../AuthProvider.js";
import { getPasswordRules } from "../passwordRules.js";
import { useResendCooldown } from "../useResendCooldown.js";
import { MAX_EMAIL_LENGTH, MAX_PASSWORD_LENGTH, MAX_CONFIRMATION_CODE_LENGTH, } from "../inputLimits.js";
import { AuthAlert } from "./AuthAlert.js";
import { AuthField } from "./AuthField.js";
import { PasswordChecklist } from "./PasswordChecklist.js";
import { SocialSignIn } from "./SocialSignIn.js";
// =============================================================================
// Default social providers
// =============================================================================
const DEFAULT_GOOGLE_PROVIDER = {
    id: "Google",
    label: "Google",
    leading: (_jsx("span", { className: "mr-2 flex size-5 items-center justify-center rounded-full border border-slate-200 bg-white text-xs font-semibold", children: "G" })),
};
// =============================================================================
// Component
// =============================================================================
export function LoginPage({ socialProviders = [DEFAULT_GOOGLE_PROVIDER], wrapper: Wrapper, signupPath = "/signup", ns = "common", }) {
    const { t } = useTranslation(ns);
    const navigate = useNavigate();
    const { isAuthed, isAuthReady, loginWith, refreshSession } = useAuth();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [resetCode, setResetCode] = useState("");
    const [newPassword, setNewPassword] = useState("");
    const [step, setStep] = useState("login");
    const [signUpConfirmCode, setSignUpConfirmCode] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [errorMessage, setErrorMessage] = useState(null);
    const [infoMessage, setInfoMessage] = useState(null);
    const [showPassword, setShowPassword] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const { startCooldown, timeLeft, isActive: resendCooldownActive } = useResendCooldown();
    const { startCooldown: startResetCooldown, timeLeft: resetTimeLeft, isActive: resetCooldownActive, } = useResendCooldown();
    React.useEffect(() => {
        if (isAuthReady && isAuthed) {
            navigate("/", { replace: true });
        }
    }, [isAuthReady, isAuthed, navigate]);
    const goToStep = (next) => {
        setErrorMessage(null);
        setInfoMessage(null);
        setStep(next);
    };
    const resetRules = useMemo(() => getPasswordRules(newPassword), [newPassword]);
    const canLogin = Boolean(email.trim() && password && !isSubmitting);
    const canRequestReset = Boolean(email.trim() && !isSubmitting);
    const canConfirmReset = Boolean(email.trim() && resetCode.trim() && resetRules.every((r) => r.valid) && !isSubmitting);
    const canConfirmSignUp = Boolean(signUpConfirmCode.trim() && !isSubmitting);
    // ─── Handlers ─────────────────────────────────────────────────────────
    const handleLogin = async () => {
        if (!canLogin)
            return;
        setIsSubmitting(true);
        setErrorMessage(null);
        setInfoMessage(null);
        try {
            const result = await signIn({ username: email.trim(), password });
            if (result.isSignedIn) {
                await refreshSession();
                navigate("/", { replace: true });
                return;
            }
            if (result.nextStep?.signInStep === "CONFIRM_SIGN_UP") {
                try {
                    await resendSignUpCode({ username: email.trim() });
                    setInfoMessage(t("auth.verificationSent"));
                }
                catch {
                    setInfoMessage(t("auth.checkEmailCode"));
                }
                setStep("confirmSignUp");
            }
            else {
                setInfoMessage(t("auth.additionalStepRequired"));
            }
        }
        catch (error) {
            setErrorMessage(error instanceof Error ? error.message : t("auth.loginFailed"));
        }
        finally {
            setIsSubmitting(false);
        }
    };
    const handleForgotPassword = async () => {
        if (!canRequestReset)
            return;
        setIsSubmitting(true);
        setErrorMessage(null);
        setInfoMessage(null);
        try {
            const result = await resetPassword({ username: email.trim() });
            if (result.nextStep.resetPasswordStep === "CONFIRM_RESET_PASSWORD_WITH_CODE") {
                setStep("reset");
                const dest = result.nextStep.codeDeliveryDetails?.destination ?? t("auth.yourEmail");
                setInfoMessage(t("auth.resetCodeSentTo", { destination: dest }));
            }
            else {
                setStep("login");
                setInfoMessage(t("auth.resetAlreadyComplete"));
            }
        }
        catch (error) {
            if (error instanceof Error && error.name === "UserNotFoundException") {
                setErrorMessage(t("auth.userNotFoundGoogle"));
            }
            else if (error instanceof Error &&
                (error.name === "InvalidParameterException" ||
                    error.message.includes("no registered/verified email"))) {
                try {
                    await resendSignUpCode({ username: email.trim() });
                    setInfoMessage(t("auth.accountNotVerifiedSent"));
                }
                catch {
                    setInfoMessage(t("auth.accountNotVerifiedCheck"));
                }
                setStep("confirmSignUp");
            }
            else {
                setErrorMessage(error instanceof Error ? error.message : t("auth.passwordResetFailed"));
            }
        }
        finally {
            setIsSubmitting(false);
        }
    };
    const handleConfirmReset = async () => {
        if (!canConfirmReset)
            return;
        setIsSubmitting(true);
        setErrorMessage(null);
        setInfoMessage(null);
        try {
            await confirmResetPassword({
                username: email.trim(),
                confirmationCode: resetCode.trim(),
                newPassword,
            });
            setStep("login");
            setNewPassword("");
            setResetCode("");
            setInfoMessage(t("auth.passwordUpdated"));
        }
        catch (error) {
            setErrorMessage(error instanceof Error ? error.message : t("auth.updateFailed"));
        }
        finally {
            setIsSubmitting(false);
        }
    };
    const handleConfirmSignUp = async () => {
        if (!canConfirmSignUp)
            return;
        setIsSubmitting(true);
        setErrorMessage(null);
        setInfoMessage(null);
        try {
            await confirmSignUp({ username: email.trim(), confirmationCode: signUpConfirmCode.trim() });
            const result = await signIn({ username: email.trim(), password });
            if (result.isSignedIn) {
                await refreshSession();
                navigate("/", { replace: true });
                return;
            }
            setStep("login");
            setInfoMessage(t("auth.accountConfirmedLogIn"));
        }
        catch (error) {
            setErrorMessage(error instanceof Error ? error.message : t("auth.confirmationFailed"));
        }
        finally {
            setIsSubmitting(false);
        }
    };
    const handleResendSignUpCode = async () => {
        setIsSubmitting(true);
        setErrorMessage(null);
        setInfoMessage(null);
        try {
            await resendSignUpCode({ username: email.trim() });
            setInfoMessage(t("auth.newCodeSent"));
            startCooldown();
        }
        catch (error) {
            setErrorMessage(error instanceof Error ? error.message : t("auth.resendCodeFailed"));
        }
        finally {
            setIsSubmitting(false);
        }
    };
    const handleResendResetCode = async () => {
        setIsSubmitting(true);
        setErrorMessage(null);
        setInfoMessage(null);
        try {
            await resetPassword({ username: email.trim() });
            setInfoMessage(t("auth.newResetCodeSent"));
            startResetCooldown();
        }
        catch (error) {
            setErrorMessage(error instanceof Error ? error.message : t("auth.resendResetFailed"));
        }
        finally {
            setIsSubmitting(false);
        }
    };
    // ─── Loading / already authed ─────────────────────────────────────────
    if (!isAuthReady || isAuthed) {
        const loading = (_jsx("div", { className: "flex min-h-[calc(100vh-128px)] items-center justify-center", children: _jsxs("div", { className: "w-full max-w-md rounded-3xl border border-slate-200 bg-white px-6 py-8 shadow-xl", children: [_jsx("div", { className: "mx-auto flex size-12 items-center justify-center rounded-full bg-violet-100 text-violet-600", children: _jsx(Lock, { className: "size-5" }) }), _jsx("p", { className: "mt-4 text-center text-sm text-slate-500", children: t("auth.checkingSession") })] }) }));
        return Wrapper ? _jsx(Wrapper, { children: loading }) : loading;
    }
    // ─── Titles ───────────────────────────────────────────────────────────
    const title = step === "login" ? t("auth.welcomeBack")
        : step === "forgot" ? t("auth.resetPassword")
            : step === "reset" ? t("auth.setNewPassword")
                : t("auth.confirmEmail");
    const subtitle = step === "login" ? t("auth.logInToContinue")
        : step === "forgot" ? t("auth.weWillEmailCode")
            : step === "reset" ? t("auth.enterCodeAndPassword")
                : t("auth.enterConfirmationCode");
    // ─── Render ───────────────────────────────────────────────────────────
    const card = (_jsx("div", { className: "flex min-h-[calc(100vh-128px)] items-center justify-center", children: _jsxs("div", { className: "relative w-full max-w-md rounded-3xl border border-slate-200 bg-white px-6 py-8 shadow-xl", children: [_jsx("button", { type: "button", "aria-label": t("auth.closeLogin"), onClick: () => navigate("/", { replace: true }), className: "absolute right-4 top-4 rounded-full p-1 text-slate-400 transition hover:text-slate-600", children: _jsx(X, { className: "size-4" }) }), _jsx("div", { className: "mx-auto flex size-12 items-center justify-center rounded-full bg-violet-100 text-violet-600", children: _jsx(Lock, { className: "size-5" }) }), _jsx("h1", { className: "mt-4 text-center text-2xl font-semibold text-slate-900", children: title }), _jsx("p", { className: "mt-2 text-center text-sm text-slate-600", children: subtitle }), errorMessage ? _jsx(AuthAlert, { variant: "error", children: errorMessage }) : null, infoMessage ? _jsx(AuthAlert, { variant: "info", children: infoMessage }) : null, step === "login" ? (_jsxs("div", { className: "mt-6 space-y-4", children: [_jsx(AuthField, { id: "login-email", label: t("auth.emailAddress"), type: "email", autoComplete: "email", placeholder: t("auth.emailPlaceholder"), value: email, onChange: setEmail, icon: _jsx(Mail, { className: "size-4" }), maxLength: MAX_EMAIL_LENGTH }), _jsxs("div", { className: "space-y-2", children: [_jsxs("div", { className: "flex items-center justify-between", children: [_jsx("label", { className: "text-sm font-medium text-slate-700", htmlFor: "login-password", children: t("auth.password") }), _jsx("button", { type: "button", onClick: () => goToStep("forgot"), className: "text-sm font-medium text-violet-600 transition-colors hover:text-violet-700", children: t("auth.forgotPassword") })] }), _jsx(AuthField, { id: "login-password", type: showPassword ? "text" : "password", autoComplete: "current-password", placeholder: t("auth.passwordPlaceholder"), value: password, onChange: setPassword, icon: _jsx(Lock, { className: "size-4" }), maxLength: MAX_PASSWORD_LENGTH, rightSlot: _jsx("button", { type: "button", "aria-label": showPassword ? t("auth.hidePassword") : t("auth.showPassword"), onClick: () => setShowPassword((p) => !p), className: "transition hover:text-slate-600", children: showPassword ? _jsx(EyeOff, { className: "size-4" }) : _jsx(Eye, { className: "size-4" }) }) })] }), _jsx("button", { type: "button", onClick: handleLogin, disabled: !canLogin, className: "h-11 w-full rounded-xl text-sm font-medium text-white transition-colors bg-violet-600 hover:bg-violet-700 disabled:cursor-not-allowed disabled:opacity-50", children: isSubmitting ? t("auth.signingIn") : t("auth.logIn") }), socialProviders.length > 0 && (_jsx(SocialSignIn, { title: t("auth.orContinueWith"), providers: socialProviders, columns: 1, onProviderLogin: loginWith })), _jsxs("p", { className: "text-center text-sm text-slate-600", children: [t("auth.dontHaveAccount"), " ", _jsx(Link, { to: signupPath, className: "font-medium text-violet-600 hover:text-violet-700", children: t("auth.signUp") })] })] })) : null, step === "forgot" ? (_jsxs("div", { className: "mt-6 space-y-4", children: [_jsx(AuthField, { id: "forgot-email", label: t("auth.emailAddress"), type: "email", autoComplete: "email", placeholder: t("auth.emailPlaceholder"), value: email, onChange: setEmail, icon: _jsx(Mail, { className: "size-4" }), maxLength: MAX_EMAIL_LENGTH }), _jsx("button", { type: "button", onClick: handleForgotPassword, disabled: !canRequestReset, className: "h-11 w-full rounded-xl text-sm font-medium text-white transition-colors bg-violet-600 hover:bg-violet-700 disabled:cursor-not-allowed disabled:opacity-50", children: isSubmitting ? t("auth.sending") : t("auth.sendResetCode") }), _jsx("button", { type: "button", onClick: () => goToStep("login"), className: "w-full text-sm font-medium text-slate-600 transition-colors hover:text-slate-900", children: t("auth.backToSignIn") })] })) : null, step === "confirmSignUp" ? (_jsxs("div", { className: "mt-6 space-y-4", children: [_jsx(AuthField, { id: "confirm-signup-code", label: t("auth.confirmationCode"), type: "text", autoComplete: "one-time-code", value: signUpConfirmCode, onChange: setSignUpConfirmCode, maxLength: MAX_CONFIRMATION_CODE_LENGTH }), _jsx("button", { type: "button", onClick: handleConfirmSignUp, disabled: !canConfirmSignUp, className: "h-11 w-full rounded-xl text-sm font-medium text-white transition-colors bg-violet-600 hover:bg-violet-700 disabled:cursor-not-allowed disabled:opacity-50", children: isSubmitting ? t("auth.confirming") : t("auth.confirmAccount") }), _jsxs("p", { className: "text-center text-sm text-slate-600", children: [t("auth.didntGetCode"), " ", _jsx("button", { type: "button", onClick: handleResendSignUpCode, disabled: isSubmitting || resendCooldownActive, className: "font-medium text-violet-600 transition-colors hover:text-violet-700 disabled:opacity-50", children: resendCooldownActive
                                        ? t("auth.resendCodeSeconds", { seconds: timeLeft })
                                        : t("auth.resendCode") })] }), _jsx("button", { type: "button", onClick: () => goToStep("login"), className: "w-full text-sm font-medium text-slate-600 transition-colors hover:text-slate-900", children: t("auth.backToSignIn") })] })) : null, step === "reset" ? (_jsxs("div", { className: "mt-6 space-y-4", children: [_jsx(AuthField, { id: "reset-code", label: t("auth.resetCode"), type: "text", autoComplete: "one-time-code", value: resetCode, onChange: setResetCode, maxLength: MAX_CONFIRMATION_CODE_LENGTH }), _jsx(AuthField, { id: "reset-password", label: t("auth.newPassword"), type: showNewPassword ? "text" : "password", autoComplete: "new-password", value: newPassword, onChange: setNewPassword, icon: _jsx(Lock, { className: "size-4" }), maxLength: MAX_PASSWORD_LENGTH, rightSlot: _jsx("button", { type: "button", "aria-label": showNewPassword ? t("auth.hidePassword") : t("auth.showPassword"), onClick: () => setShowNewPassword((p) => !p), className: "transition hover:text-slate-600", children: showNewPassword ? _jsx(EyeOff, { className: "size-4" }) : _jsx(Eye, { className: "size-4" }) }) }), _jsx(PasswordChecklist, { rules: resetRules }), _jsx("button", { type: "button", onClick: handleConfirmReset, disabled: !canConfirmReset, className: "h-11 w-full rounded-xl text-sm font-medium text-white transition-colors bg-violet-600 hover:bg-violet-700 disabled:cursor-not-allowed disabled:opacity-50", children: isSubmitting ? t("auth.updatingPassword") : t("auth.updatePassword") }), _jsxs("p", { className: "text-center text-sm text-slate-600", children: [t("auth.didntGetCode"), " ", _jsx("button", { type: "button", onClick: handleResendResetCode, disabled: isSubmitting || resetCooldownActive, className: "font-medium text-violet-600 transition-colors hover:text-violet-700 disabled:opacity-50", children: resetCooldownActive
                                        ? t("auth.resendCodeSeconds", { seconds: resetTimeLeft })
                                        : t("auth.resendCode") })] }), _jsx("button", { type: "button", onClick: () => goToStep("login"), className: "w-full text-sm font-medium text-slate-600 transition-colors hover:text-slate-900", children: t("auth.backToSignIn") })] })) : null] }) }));
    return Wrapper ? _jsx(Wrapper, { children: card }) : card;
}
